import IconFirstPage from '@apollo/icons/large/IconChevronEndLeft.svg';
import IconLastPage from '@apollo/icons/large/IconChevronEndRight.svg';
import IconArrowLeft from '@apollo/icons/large/IconChevronLeft.svg';
import IconArrowRight from '@apollo/icons/large/IconChevronRight.svg';
import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { format } from 'src/lib/format';

interface PaginationControlsTypes {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  numPages: number;
  loading?: boolean;
  disabled?: boolean;
  onFirst: (nextPage: number) => void;
  onPrevious: (nextPage: number) => void;
  onNext: (nextPage: number) => void;
  onLast: (nextPage: number) => void;
}

export const PaginationControls = ({
  currentPage,
  totalCount,
  pageSize,
  numPages,
  loading,
  disabled,
  onFirst,
  onPrevious,
  onNext,
  onLast,
}: PaginationControlsTypes) => {
  const recordsBoundsStartIndex = Math.min(
    1 + pageSize * currentPage,
    totalCount,
  );
  const recordsBoundsEndIndex = Math.min(
    pageSize * (currentPage + 1),
    totalCount,
  );
  return (
    <div className="relative z-[10] flex h-[4rem] w-full flex-nowrap content-center items-center justify-between overflow-hidden whitespace-nowrap border-t-[1px] border-primary pt-4">
      <div>
        <Tooltip label="First">
          <Button
            onClick={() => {
              onFirst(0);
            }}
            variant="hidden"
            isDisabled={currentPage === 0}
            leftIcon={<IconFirstPage />}
            aria-label="First"
          />
        </Tooltip>
        <Tooltip label="Previous">
          <Button
            className="ml-1"
            onClick={() => {
              // Don't allow the user to paginate to the previous page if
              // they're already on the first page
              onPrevious(currentPage === 0 ? 0 : currentPage - 1);
            }}
            variant="hidden"
            isDisabled={currentPage === 0}
            leftIcon={<IconArrowLeft />}
            aria-label="Previous"
          />
        </Tooltip>
      </div>
      <div
        className={classnames(
          'flex flex-nowrap content-center items-center text-black',
          { 'motion-safe:animate-pulse-faded': loading || disabled },
        )}
      >
        <span className="px-0.75 font-semibold text-primary">
          {recordsBoundsStartIndex}-{recordsBoundsEndIndex}
        </span>{' '}
        <span className="text-primary">
          of {format.number(totalCount, '0,0')}
        </span>
      </div>
      <div>
        <Tooltip label="Next">
          <Button
            className="mr-1"
            onClick={() => {
              // Don't allow the user to paginate to the next page if they're
              // already on the last page
              onNext(currentPage === numPages ? currentPage : currentPage + 1);
            }}
            variant="hidden"
            isDisabled={currentPage === numPages}
            leftIcon={<IconArrowRight />}
            aria-label="Next"
          />
        </Tooltip>
        <Tooltip label="Last">
          <Button
            onClick={() => {
              onLast(numPages);
            }}
            variant="hidden"
            isDisabled={currentPage === numPages}
            leftIcon={<IconLastPage />}
            aria-label="Last"
          />
        </Tooltip>
      </div>
    </div>
  );
};
